import { getManageDependentsForm } from 'api/operations/formService';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { useEffect, useState } from 'react';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import { SubscriberPersonInfo } from '../types';
import SubscriberBasicInfoDelegate from '../subscriberBasicInfoDelegate';

interface DomainData {
  formId: string;
  subscriberBasicInfo: SubscriberBasicInfoDelegate;
}

export class AddDependentModalStore {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _subscriberBasicInfo: SubscriberBasicInfoDelegate;
  private readonly _formId: string;
  private _addDependentModal = new ModalDelegate();

  public dynamicFormDelegate = new DynamicFormDelegate({
    currentUserRoles: this._rootStore.userStore.userRoles,
    fetchForm: async (formId: string): Promise<FormObjectFromJSON> => {
      return getManageDependentsForm(
        formId,
        {
          common: this._subscriberBasicInfo.primary,
          custom: { dependents: this._subscriberBasicInfo.basicInfo.dependents },
        },
        null,
        this._rootStore.userStore.sponsorId,
      );
    },
    onSubmit: this.addDependent.bind(this),
  });

  public get showAddDependentModal(): boolean {
    return this._addDependentModal.isVisible;
  }

  public async openAddDependentModal(): Promise<void> {
    this._addDependentModal.open();
    await this.dynamicFormDelegate.buildForm(this._formId);
  }

  public closeAddDependentModal(): void {
    this.dynamicFormDelegate.resetState();
    this._addDependentModal.close();
  }

  public async addDependent(): Promise<void> {
    if (!this.dynamicFormDelegate.formController) return;

    const newDependentInfo = {
      ...this.dynamicFormDelegate.formAnswers,
      id: '',
    } as unknown as SubscriberPersonInfo;
    await this._subscriberBasicInfo.addDependent(newDependentInfo);

    runInAction(() => {
      this.closeAddDependentModal();
    });
  }

  constructor({ formId, subscriberBasicInfo }: DomainData) {
    this._formId = formId;
    this._subscriberBasicInfo = subscriberBasicInfo;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export function useAddDependentModalStore({ subscriberBasicInfo, formId }: DomainData): AddDependentModalStore | null {
  const [store, setStore] = useState<AddDependentModalStore | null>(
    () => new AddDependentModalStore({ subscriberBasicInfo, formId }),
  );

  useEffect(() => {
    autorun(() => {
      setStore(new AddDependentModalStore({ subscriberBasicInfo, formId }));
    });

    return (): void => {
      setStore(null);
    };
  }, [subscriberBasicInfo, formId]);
  return store;
}
